import { Link } from "react-router-dom";
import { MaskedLines, RuleDraw } from "./Reveal";

export function Breadcrumbs({ trail }) {
    return (
        <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
                {trail.map((t, i) => (
                    <li key={i} className="flex items-center gap-2">
                        {i > 0 && <span aria-hidden="true" className="text-line-2">/</span>}
                        {t.path ? (
                            <Link to={t.path} className="transition-colors hover:text-blue">
                                {t.label}
                            </Link>
                        ) : (
                            <span aria-current="page" className="text-ink-2">{t.label}</span>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export function PageHero({ eyebrow, lines, sub, trail, image, imageAlt = "", dark = true }) {
    return (
        <header className={`relative overflow-hidden ${dark ? "bg-navy text-white" : "bg-paper text-ink"}`}>
            {image && (
                <>
                    <img
                        src={image}
                        alt={imageAlt}
                        className="img-grade absolute inset-0 h-full w-full object-cover opacity-40"
                        loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/40" />
                </>
            )}
            <div className="container-x relative pb-16 pt-36 sm:pb-20 sm:pt-44 lg:pb-24 lg:pt-52">
                {trail && (
                    <div className={dark ? "text-white/50" : ""}>
                        <Breadcrumbs trail={trail} />
                    </div>
                )}
                {eyebrow && <p className={`${dark ? "eyebrow-light" : "eyebrow"} mb-6`}>{eyebrow}</p>}
                <MaskedLines
                    lines={lines}
                    className={`h1-display max-w-5xl ${dark ? "text-white" : ""}`}
                    lineClassName={dark ? "text-white" : ""}
                />
                {sub && <p className={`body-lg mt-8 max-w-2xl ${dark ? "text-white/70" : ""}`}>{sub}</p>}
                <RuleDraw light={dark} className="mt-12" delay={0.6} />
            </div>
        </header>
    );
}

export function SectionHead({ eyebrow, title, intro, dark = false, className = "" }) {
    return (
        <div className={className}>
            {eyebrow && <p className={`${dark ? "eyebrow-light" : "eyebrow"} mb-5`}>{eyebrow}</p>}
            <h2 className={`h2-section max-w-3xl ${dark ? "text-white" : ""}`}>{title}</h2>
            {intro && (
                <p className={`body-lg mt-6 max-w-2xl ${dark ? "text-white/70" : ""}`}>{intro}</p>
            )}
        </div>
    );
}

export function Stat({ value, unit, label, prefix, dark = false, size = "lg" }) {
    return (
        <div data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <p className={`stat-numeral leading-none ${size === "lg" ? "text-5xl sm:text-6xl lg:text-7xl" : "text-4xl sm:text-5xl"} ${dark ? "text-white" : ""}`}>
                {prefix && <span className="mr-2 align-top font-mono text-sm font-medium tracking-normal opacity-60">{prefix}</span>}
                {value}
                {unit && <span className={`ml-1 align-top font-mono text-base font-medium tracking-normal sm:text-lg ${dark ? "text-blue-bright" : "text-blue"}`}>{unit}</span>}
            </p>
            <p className={`mt-3 font-mono text-[11px] uppercase tracking-[0.2em] ${dark ? "text-white/50" : "text-ink-3"}`}>{label}</p>
        </div>
    );
}

export function CtaBand({ title = "Let's discuss the infrastructure behind Europe's energy transition.", cta = "Contact us", to = "/contact" }) {
    return (
        <section className="bg-navy text-white">
            <div className="container-x py-20 sm:py-24 lg:py-28">
                <div className="grid items-end gap-10 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <p className="eyebrow-light mb-6">Contact</p>
                        <h2 className="h2-section text-white">{title}</h2>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link to={to} data-testid="cta-band-button" className="btn-solid-light">
                            {cta}
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

export function ImageBlock({ src, alt, caption, ratio = "aspect-[16/9]" }) {
    return (
        <figure className="group overflow-hidden" data-testid="image-block">
            <div className={`${ratio} overflow-hidden`}>
                <img
                    src={src}
                    alt={alt}
                    loading="lazy"
                    className="img-grade h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.015]"
                />
            </div>
            {caption && (
                <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-ink-3">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
}
